/**
 * Session — the store seam. Every Firebase Auth call and every Firestore
 * read/write session assembly needs, wrapped as plain async functions over
 * the host's own Firebase instances (its one app, from @momo/firebase-client);
 * session.sync.ts is the only caller.
 */

import { passkeyCeremony } from "@mhmo91/passkey/client";
import {
	IdentityOtp,
	ResolveEmail,
	Role,
	SendOTP,
	User,
	VerifyOTP,
} from "@momo/types";
import { uiLanguage } from "@momo/views/localize";
import { FirebaseError } from "firebase/app";
import {
	type Auth,
	type User as FirebaseUser,
	signInAnonymously,
	signInWithCustomToken,
	signOut,
} from "firebase/auth";
import {
	doc,
	type Firestore,
	getDoc,
	setDoc,
	Timestamp,
	updateDoc,
} from "firebase/firestore";
import { type Functions, httpsCallable } from "firebase/functions";

/** The Firebase instances a host already holds: its one app's Auth,
 *  Firestore and Functions — the seam never initializes an app of its own. */
export type SessionFirebase = {
	readonly auth: Auth;
	readonly db: Firestore;
	readonly functions: Functions;
};

/** Thrown when sendOTP.api.ts or resolveEmail.api.ts refuses with
 *  `resource-exhausted`: this address has asked for too many codes in the
 *  current window, or this caller has resolved too many addresses. Waiting
 *  is the remedy, where any other refusal (an unreachable network among them
 *  — the callable SDK reports that as `internal`, the same as a server
 *  fault) is retried at once. */
export class RateLimitedError extends Error {}

/** Thrown when verifyOTP.api.ts's `failed-precondition/invite-expired`
 *  fires — the submitted code was correct but the invite backing it has
 *  since lapsed. Rethrown here (from the raw `FirebaseError` the callable
 *  SDK surfaces) so session.sync.ts's catchError never needs to import a
 *  Firebase SDK type directly to tell this apart from an ordinary
 *  wrong/expired code. */
export class InviteExpiredError extends Error {}

export function sessionApi({ auth, db, functions }: SessionFirebase) {
	async function signInAnonymousSession(): Promise<void> {
		await signInAnonymously(auth);
	}

	async function signOutSession(): Promise<void> {
		await signOut(auth);
	}

	/** The sign-in door's one callable; each act is an `action` on its payload
	 *  (identityOtp.action.ts), parsed `.strict()` before it crosses. */
	const identityOtp = httpsCallable<
		IdentityOtp.IdentityOtpInput,
		IdentityOtp.IdentityOtpOutput
	>(functions, "identityOtp");

	/** Would this address be mailed a code? The deliberate, bounded answer
	 *  the email step branches on before any code is asked for. A throttled
	 *  caller is told to wait, as a throttled send is. */
	async function resolveEmail(
		orgId: string | null,
		email: string,
	): Promise<ResolveEmail.ResolveEmailOutput> {
		const response = await identityOtp(
			IdentityOtp.IdentityOtpInput.parse({
				action: "resolveEmail",
				orgId,
				email,
			}),
		).catch((err: unknown) => {
			if (
				err instanceof FirebaseError &&
				err.code === "functions/resource-exhausted"
			) {
				throw new RateLimitedError(err.message);
			}
			throw err;
		});
		return ResolveEmail.ResolveEmailOutput.parse(response.data);
	}

	/**
	 * The response is deliberately generic — never reveals whether the address
	 * exists. `locale` is the active UI language — the one the visitor chose,
	 * not the browser's — so the login-code email speaks the language the
	 * sign-in screen itself is showing, pre-auth — there is no user doc yet.
	 */
	async function sendOtp(
		orgId: string | null,
		email: string,
	): Promise<SendOTP.SendOTPOutput> {
		const locale = uiLanguage();
		const response = await identityOtp(
			IdentityOtp.IdentityOtpInput.parse({
				action: "sendOTP",
				orgId,
				email,
				locale,
			}),
		).catch((err: unknown) => {
			if (
				err instanceof FirebaseError &&
				err.code === "functions/resource-exhausted"
			) {
				throw new RateLimitedError(err.message);
			}
			throw err;
		});
		return SendOTP.SendOTPOutput.parse(response.data);
	}

	/** The caller must NOT navigate for a new user — it shows the consent screen. */
	async function verifyOtp(
		orgId: string | null,
		email: string,
		code: string,
	): Promise<VerifyOTP.VerifyOTPOutput> {
		const response = await identityOtp(
			IdentityOtp.IdentityOtpInput.parse({
				action: "verifyOTP",
				orgId,
				email,
				code,
			}),
		).catch((err: unknown) => {
			if (
				err instanceof FirebaseError &&
				err.code === "functions/failed-precondition"
			) {
				if (err.message === "invite-expired")
					throw new InviteExpiredError(err.message);
			}
			throw err;
		});
		const result = VerifyOTP.VerifyOTPOutput.parse(response.data);
		await signInWithCustomToken(auth, result.token);
		return result;
	}

	async function writeUserConsent(input: {
		uid: string;
		email: string;
		displayName: string;
		workspaceId: string | null;
		tosVersion: string;
		privacyVersion: string;
		/** The texts accepted are the deployment operator's, not an
		 *  organization's — the bare host's consent. */
		platform: boolean;
	}): Promise<void> {
		// A concrete client Timestamp, not the serverTimestamp() sentinel —
		// Firestore rejects sentinels inside arrays, and legalConsents is one.
		const acceptedAt = Timestamp.now();
		const userAgent = navigator.userAgent;
		const payload = User.UserSchema.parse({
			uid: input.uid,
			email: input.email,
			displayName: input.displayName,
			legalConsents: [
				{
					kind: "tos",
					version: input.tosVersion,
					acceptedAt,
					userAgent,
					platform: input.platform,
				},
				{
					kind: "privacy",
					version: input.privacyVersion,
					acceptedAt,
					userAgent,
					platform: input.platform,
				},
			],
			language: null,
			// The server never creates users/{uid}, so the organization verifyOTP
			// opened is saved here, at the record's birth.
			workspaceId: input.workspaceId,
			activeEventId: null,
			createdAt: acceptedAt,
			updatedAt: acceptedAt,
		});
		await setDoc(doc(db, "users", input.uid), payload);
	}

	/** Saves the organization a sign-in opened onto a returning person's record
	 *  that names none. Clearing the event is what the record's rule asks of a
	 *  changed organization. */
	async function adoptOrganizationIfUnset(
		uid: string,
		workspaceId: string,
	): Promise<void> {
		const ref = doc(db, "users", uid);
		const snap = await getDoc(ref);
		if (!snap.exists()) return;
		if (
			User.UserSchema.pick({ workspaceId: true }).parse(snap.data()).workspaceId
		)
			return;
		await updateDoc(ref, { workspaceId, activeEventId: null });
	}

	async function fetchRole(
		orgId: string,
		roleId: string,
	): Promise<Role.Role | null> {
		const snap = await getDoc(doc(db, "workspaces", orgId, "roles", roleId));
		return snap.exists() ? Role.RoleSchema.parse(snap.data()) : null;
	}

	async function fetchMfaClaim(user: FirebaseUser): Promise<boolean> {
		const token = await user.getIdTokenResult();
		return token.claims.mfa === true;
	}

	const { enrollPasskey, signInWithPasskey } = passkeyCeremony({
		auth,
		functions,
		callable: "identityPasskey",
		rpName: location.hostname,
	});

	return {
		enrollPasskey,
		signInWithPasskey,
		adoptOrganizationIfUnset,
		fetchMfaClaim,
		fetchRole,
		resolveEmail,
		sendOtp,
		signInAnonymousSession,
		signOutSession,
		verifyOtp,
		writeUserConsent,
	};
}

export type SessionApi = ReturnType<typeof sessionApi>;
